import { type FC, useEffect, useId } from 'react'
import { useIntl } from 'react-intl'

import { FormControl, FormLabel, Select } from 'base-ui'

import { type ContactFormState } from '../../../contact-form'
import { type OpenableControls } from '../../../generic-hooks'
import { useGlobalConfigContext } from '../../../global-config'
import { directContactFormAccessorGetSubjects } from '../../model/directContactFormAccessorGetSubjects'
import { directContactFormAccessorIsProductType } from '../../model/directContactFormAccessorIsProductType'

import { useDirectFormDialogContext } from './DirectContactFormDialogContext'
import { DirectContactFormProductsInputField } from './DirectContactFormProductsInputField'

export const DirectContactFormPrefilledOrderInputField: FC<{
  formState: ContactFormState
  productSelectionModalControls: OpenableControls
}> = ({ formState, productSelectionModalControls }) => {
  const intl = useIntl()
  const { tenant } = useGlobalConfigContext()
  const { prefilledSubOrder, productList, subOrderNumber } =
    useDirectFormDialogContext()

  const problemMeldenSubject = intl.formatMessage({
    id: 'direct.contact.form.report.order',
  })

  const requestCancellationSubject = intl.formatMessage({
    id: 'direct.contact.form.order.cancellation',
  })

  const returnOrderSubject = intl.formatMessage({
    id: 'direct.contact.form.return.order',
  })

  const orderWithMissingOrIncorrectItem = intl.formatMessage({
    id: 'direct.contact.form.order.missing.or.incorrect.item',
  })

  const subjectsWithProductSelection = [
    requestCancellationSubject,
    problemMeldenSubject,
    returnOrderSubject,
    orderWithMissingOrIncorrectItem,
  ]

  const subOrderNumberId = useId()

  const { orderType } = directContactFormAccessorGetSubjects({
    intl,
    option: formState.model.subject,
    tenant,
  })
  useEffect(() => {
    if (subOrderNumber) {
      formState.setValue(subOrderNumber, formState.model, 'orderId')
      const productCode = [...productList].map((item) => item.pzn).join(', ')
      if (productCode) {
        formState.setValue(productCode, formState.model, 'productCode')
      }
    }
  }, [formState, productList, subOrderNumber, intl, prefilledSubOrder])

  if (orderType) {
    return (
      <div className="mb-2 flex flex-col justify-between gap-2 tablet:flex-row">
        <FormControl className="w-full" disabled>
          <FormLabel className="font-medium" htmlFor={subOrderNumberId}>
            {subOrderNumber}
          </FormLabel>
          <Select
            aria-labelledby={subOrderNumberId}
            id={subOrderNumberId}
            slotProps={{
              root: {
                'aria-label': intl.formatMessage({
                  id: 'direct.contact.form.order.number',
                }),
              },
            }}
          />
        </FormControl>

        {subOrderNumber &&
        formState.model.subject &&
        subjectsWithProductSelection.includes(formState.model.subject) &&
        directContactFormAccessorIsProductType(
          intl,
          formState.model.subject ?? '',
        ) ? (
          <DirectContactFormProductsInputField
            formState={formState}
            productSelectionModalControls={productSelectionModalControls}
          />
        ) : null}
      </div>
    )
  }

  return null
}
